/**
 * MemberDetailPage - Member detail at /company/:slug/members/:memberId
 *
 * Displays member profile, diagnosis axes, team radar overlay,
 * and allows linking a diagnosis if not yet diagnosed.
 * BtoB-oriented language only (no fortune-telling or astrology references).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import companyService from '../../services/companyService';
import dynamicStructCodeService from '../../services/dynamicStructCodeService';
import SpectrumBar from '../../components/company/SpectrumBar';
import RadarChart from '../../components/company/RadarChart';
import PersonalAiReport from '../../components/PersonalAiReport';

/* ================================================================== */
/*  Constants                                                          */
/* ================================================================== */

const AXIS_KEYS = ['activation', 'judgment', 'choice', 'resonance', 'awareness'];

const STATUS_CONFIG = {
  active: { label: 'Active', colorKey: 'success', fallback: '#10B981' },
  invited: { label: 'Invited', colorKey: 'warning', fallback: '#F59E0B' },
  inactive: { label: 'Inactive', colorKey: 'textMuted', fallback: '#6B7280' },
};

/* ================================================================== */
/*  Keyframes                                                          */
/* ================================================================== */

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const toastSlide = keyframes`
  0%   { opacity: 0; transform: translateY(16px); }
  10%  { opacity: 1; transform: translateY(0); }
  90%  { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-8px); }
`;

/* ================================================================== */
/*  Styled components                                                  */
/* ================================================================== */

const PageContainer = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 48px 24px 80px;
  animation: ${fadeIn} 0.35s ease-out;

  @media (max-width: ${(p) => p.theme.breakpoints?.mobile || '640px'}) {
    padding: 24px 16px 64px;
  }
`;

const Toast = styled.div`
  position: fixed;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  background: ${(p) => p.theme.colors.success || '#10B981'};
  color: #fff;
  padding: 10px 24px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  font-size: 0.85rem;
  font-weight: 600;
  pointer-events: none;
  z-index: 1000;
  animation: ${toastSlide} 2s ease forwards;
`;

/* ---- Breadcrumb ---- */

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.textSecondary};
  flex-wrap: wrap;
`;

const BreadcrumbLink = styled(Link)`
  color: ${(p) => p.theme.colors.primary};
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const BreadcrumbSep = styled.span`
  color: ${(p) => p.theme.colors.textSecondary};
  user-select: none;
`;

const BreadcrumbCurrent = styled.span`
  color: ${(p) => p.theme.colors.text};
  font-weight: 600;
`;

/* ---- Profile card ---- */

const ProfileCard = styled.div`
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius?.lg || '12px'};
  padding: 28px;
  margin-bottom: 24px;
`;

const ProfileTop = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 16px;

  @media (max-width: ${(p) => p.theme.breakpoints?.mobile || '640px'}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }
`;

const Avatar = styled.div`
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  border-radius: ${(p) => p.theme.borderRadius?.full || '9999px'};
  background: ${(p) => p.theme.colors.gradient || 'linear-gradient(135deg, #6366F1, #8B5CF6)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
`;

const ProfileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProfileName = styled.h1`
  font-size: 1.4rem;
  font-weight: 800;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 4px 0;
  line-height: 1.2;

  @media (max-width: ${(p) => p.theme.breakpoints?.mobile || '640px'}) {
    font-size: 1.2rem;
  }
`;

const ProfileEmail = styled.div`
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.textSecondary};
  margin-bottom: 8px;
`;

const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const RoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 12px;
  border-radius: ${(p) => p.theme.borderRadius?.full || '9999px'};
  background: ${(p) =>
    p.$admin
      ? (p.theme.colors.secondaryGlow || 'rgba(139,92,246,0.15)')
      : (p.theme.colors.bgTertiary)};
  border: 1px solid ${(p) =>
    p.$admin ? p.theme.colors.secondary : p.theme.colors.border};
  font-size: 0.72rem;
  font-weight: 700;
  color: ${(p) =>
    p.$admin ? p.theme.colors.secondary : p.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 12px;
  border-radius: ${(p) => p.theme.borderRadius?.full || '9999px'};
  font-size: 0.72rem;
  font-weight: 600;
  background: ${(p) => p.$bg || 'rgba(99,102,241,0.12)'};
  color: ${(p) => p.$fg || '#6366F1'};
`;

const TypeCodeBadge = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 3px 14px;
  border-radius: ${(p) => p.theme.borderRadius?.full || '9999px'};
  background: ${(p) => p.theme.colors.primaryGlow || 'rgba(99,102,241,0.12)'};
  border: 1px solid ${(p) => p.theme.colors.primary};
  font-family: ${(p) => p.theme.fonts?.mono};
  font-size: 0.82rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.primary};
  text-decoration: none;
  &:hover { opacity: 0.8; }
`;

const StructCodeDisplay = styled.div`
  margin-top: 12px;
  padding: 10px 16px;
  background: ${(p) => p.theme.colors.bgTertiary};
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  font-family: ${(p) => p.theme.fonts?.mono};
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.text};
  letter-spacing: 0.5px;
`;

/* ---- Section card ---- */

const SectionCard = styled.div`
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius?.lg || '12px'};
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 20px 0;
`;

const ChartCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  color: ${(p) => p.theme.colors.textSecondary};
`;

const LegendDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: ${(p) => p.theme.borderRadius?.full || '9999px'};
  background: ${(p) => p.$color};
`;

const DiffLabel = styled.div`
  text-align: center;
  font-size: 0.82rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.textSecondary};
  margin-top: 8px;
`;

/* ---- Link diagnosis section ---- */

const LinkDiagnosisSection = styled.div`
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius?.lg || '12px'};
  padding: 24px;
  margin-bottom: 24px;
`;

const LinkDescription = styled.p`
  font-size: 0.88rem;
  color: ${(p) => p.theme.colors.textSecondary};
  margin: 0 0 16px 0;
  line-height: 1.5;
`;

const InputRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  @media (max-width: ${(p) => p.theme.breakpoints?.mobile || '640px'}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TextInput = styled.input`
  flex: 1;
  padding: 8px 14px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  background: ${(p) => p.theme.colors.bgTertiary};
  border: 1px solid ${(p) => p.theme.colors.border};
  color: ${(p) => p.theme.colors.text};
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s;
  &:focus { border-color: ${(p) => p.theme.colors.primary}; }
  &::placeholder { color: ${(p) => p.theme.colors.textSecondary}; opacity: 0.6; }
`;

const PrimaryButton = styled.button`
  padding: 8px 20px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  background: ${(p) => p.theme.colors.primary};
  color: #fff;
  border: none;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
  &:hover { background: ${(p) => p.theme.colors.secondary}; }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  margin-top: 12px;
  padding: 10px 16px;
  background: ${(p) => `${p.theme.colors.success || '#10B981'}15`};
  border: 1px solid ${(p) => p.theme.colors.success || '#10B981'};
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  color: ${(p) => p.theme.colors.success || '#10B981'};
  font-size: 0.85rem;
  font-weight: 600;
`;

/* ---- Empty state ---- */

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 24px;
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: 0.88rem;
  line-height: 1.6;
`;

/* ---- Loading skeleton ---- */

const SkeletonBlock = styled.div`
  background: linear-gradient(
    90deg,
    ${(p) => p.theme.colors.bgTertiary} 25%,
    ${(p) => p.theme.colors.bgSecondary} 50%,
    ${(p) => p.theme.colors.bgTertiary} 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s infinite;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  height: ${(p) => p.$h || '20px'};
  width: ${(p) => p.$w || '100%'};
  margin-bottom: ${(p) => p.$mb || '0'};
`;

/* ---- Error state ---- */

const ErrorContainer = styled.div`
  text-align: center;
  padding: 64px 24px;
`;

const ErrorTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 12px 0;
`;

const ErrorMessage = styled.p`
  font-size: 0.9rem;
  color: ${(p) => p.theme.colors.textSecondary};
  margin: 0 0 24px 0;
`;

const RetryButton = styled.button`
  padding: 10px 24px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  background: ${(p) => p.theme.colors.primary};
  color: #fff;
  border: none;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: ${(p) => p.theme.colors.secondary}; }
`;

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

function useAxisNames() {
  const { t } = useLanguage();
  return {
    activation: t('行動特性', 'Behavioral'),
    judgment: t('判断基準', 'Decision'),
    choice: t('価値観', 'Values'),
    resonance: t('共感性', 'Empathy'),
    awareness: t('自己認識', 'Self-Awareness'),
  };
}

/* ================================================================== */
/*  Loading skeleton                                                   */
/* ================================================================== */

function LoadingSkeleton() {
  return (
    <PageContainer>
      <SkeletonBlock $h="16px" $w="240px" $mb="24px" />
      <SectionCard>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
          <SkeletonBlock $h="64px" $w="64px" style={{ borderRadius: '50%', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <SkeletonBlock $h="24px" $w="180px" $mb="8px" />
            <SkeletonBlock $h="14px" $w="200px" $mb="8px" />
            <SkeletonBlock $h="22px" $w="140px" />
          </div>
        </div>
      </SectionCard>
      <SectionCard>
        <SkeletonBlock $h="14px" $w="160px" $mb="20px" />
        {[0, 1, 2, 3, 4].map((i) => (
          <SkeletonBlock key={i} $h="18px" $w="100%" $mb="10px" />
        ))}
      </SectionCard>
      <SectionCard style={{ minHeight: 280 }}>
        <SkeletonBlock $h="14px" $w="180px" $mb="20px" />
        <SkeletonBlock $h="200px" $w="200px" style={{ margin: '0 auto', borderRadius: '50%' }} />
      </SectionCard>
    </PageContainer>
  );
}

/* ================================================================== */
/*  Main component                                                     */
/* ================================================================== */

function MemberDetailPage() {
  const { slug, memberId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { isAuthenticated } = useAuth();
  const axisNames = useAxisNames();

  /* ---- State ---- */
  const [company, setCompany] = useState(null);
  const [member, setMember] = useState(null);
  const [teamAnalysis, setTeamAnalysis] = useState(null);
  const [personalAi, setPersonalAi] = useState(null);
  const [personalAiLoading, setPersonalAiLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toastText, setToastText] = useState('');

  /* ---- Link diagnosis state ---- */
  const [diagnosisIdInput, setDiagnosisIdInput] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkSuccess, setLinkSuccess] = useState(false);

  /* ---- Toast helper ---- */
  const showToast = useCallback((msg) => {
    setToastText(msg);
    setTimeout(() => setToastText(''), 2200);
  }, []);

  /* ---- Data fetching ---- */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const companyData = await companyService.getCompany(slug);
      setCompany(companyData);

      // Find the member from company.members or fetch members list
      const membersData = await companyService.getMembers(slug);
      const found = membersData.find(
        (m) => String(m.id) === String(memberId)
      );

      if (!found) {
        setError(t('メンバーが見つかりませんでした', 'Member not found'));
        setLoading(false);
        return;
      }

      setMember(found);

      // If member has a team, fetch team analysis for overlay
      if (found.team_id) {
        try {
          const ta = await companyService.getTeamAnalysis(slug, found.team_id);
          setTeamAnalysis(ta);
        } catch {
          // Team analysis may not exist yet - that is fine
          setTeamAnalysis(null);
        }
      }

      // Fetch personal AI analysis if member has a diagnosis
      if (found.diagnosis_id) {
        setPersonalAiLoading(true);
        try {
          const cached = await dynamicStructCodeService.getCachedPersonalAiAnalysis(found.diagnosis_id);
          if (cached && cached.result) {
            setPersonalAi(cached.result);
          }
        } catch {
          // Not available yet - that is fine
        }
        setPersonalAiLoading(false);
      }
    } catch (err) {
      setError(err.message || t('データの取得に失敗しました', 'Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  }, [slug, memberId, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ---- Link diagnosis ---- */
  const handleLinkDiagnosis = useCallback(async () => {
    if (!diagnosisIdInput.trim()) return;
    setLinkLoading(true);
    setLinkSuccess(false);
    try {
      await companyService.linkDiagnosis(slug, memberId, diagnosisIdInput.trim());
      setLinkSuccess(true);
      showToast(t('分析結果を紐付けました', 'Diagnosis linked successfully'));
      // Refresh data to show new diagnosis
      setTimeout(() => fetchData(), 800);
    } catch (err) {
      showToast(
        err.message || t('紐付けに失敗しました', 'Failed to link diagnosis')
      );
    } finally {
      setLinkLoading(false);
    }
  }, [diagnosisIdInput, slug, memberId, t, showToast, fetchData]);

  /* ---- Derived: does member have diagnosis data? ---- */
  const hasDiagnosis = member?.axes && Object.keys(member.axes).length > 0;

  /* ---- Radar chart data for member ---- */
  const memberRadarData = useMemo(() => {
    if (!hasDiagnosis || !member?.axes) return [];
    return AXIS_KEYS.map((key) => {
      const raw = member.axes[key] ?? 0;
      // Normalise: if values are > 1, they are likely raw scores (0-1000)
      const isNormalized = Object.values(member.axes).every(
        (v) => typeof v === 'number' && v <= 1
      );
      const value = isNormalized ? raw : raw / 1000;
      return { label: axisNames[key], value: Math.min(Math.max(value, 0), 1) };
    });
  }, [member, hasDiagnosis, axisNames]);

  /* ---- Radar overlay: team average ---- */
  const teamOverlayData = useMemo(() => {
    if (!teamAnalysis?.centroid) return null;
    const data = AXIS_KEYS.map((key) => ({
      label: axisNames[key],
      value: Math.min(Math.max(teamAnalysis.centroid[key] ?? 0, 0), 1),
    }));
    return data;
  }, [teamAnalysis, axisNames]);

  /* ---- Status styling ---- */
  const statusCfg = STATUS_CONFIG[member?.status] || STATUS_CONFIG.active;
  const statusLabel =
    member?.status === 'active'
      ? t('アクティブ', 'Active')
      : member?.status === 'invited'
      ? t('招待中', 'Invited')
      : t('無効', 'Inactive');

  /* ---- Team name ---- */
  const teamName = useMemo(() => {
    if (!member?.team_id || !company?.teams) return null;
    const team = company.teams.find((tm) => tm.id === member.team_id);
    return team?.name || null;
  }, [member, company]);

  /* ================================================================ */
  /*  Render states                                                    */
  /* ================================================================ */

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorTitle>{t('ログインが必要です', 'Login Required')}</ErrorTitle>
          <ErrorMessage>{t('このページを利用するにはログインまたは新規登録が必要です。', 'Please log in or sign up to access this page.')}</ErrorMessage>
          <RetryButton onClick={() => navigate('/')}>
            {t('ログイン / 新規登録', 'Login / Sign Up')}
          </RetryButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  if (loading) return <LoadingSkeleton />;

  if (error) {
    const isAuthError = error === 'Not authenticated' || error === 'Invalid or expired token';
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorTitle>{isAuthError ? t('ログインが必要です', 'Login Required') : t('エラーが発生しました', 'An Error Occurred')}</ErrorTitle>
          <ErrorMessage>{isAuthError ? t('このページを利用するにはログインまたは新規登録が必要です。', 'Please log in or sign up to access this page.') : error}</ErrorMessage>
          <RetryButton onClick={isAuthError ? () => navigate('/') : fetchData}>
            {isAuthError ? t('ログイン / 新規登録', 'Login / Sign Up') : t('再試行', 'Retry')}
          </RetryButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  if (!member) {
    return (
      <PageContainer>
        <EmptyState>
          {t('メンバーが見つかりませんでした。', 'Member not found.')}
        </EmptyState>
      </PageContainer>
    );
  }

  const firstLetter = member.display_name
    ? member.display_name.charAt(0)
    : '?';

  /* ================================================================ */
  /*  Main render                                                      */
  /* ================================================================ */

  return (
    <PageContainer>
      {toastText && <Toast>{toastText}</Toast>}

      {/* ──── Breadcrumb ──── */}
      <Breadcrumb>
        <BreadcrumbLink to={`/company/${slug}`}>
          {company?.name || t('組織', 'Company')}
        </BreadcrumbLink>
        <BreadcrumbSep>/</BreadcrumbSep>
        <BreadcrumbLink to={`/company/${slug}/members`}>
          {t('メンバー', 'Members')}
        </BreadcrumbLink>
        <BreadcrumbSep>/</BreadcrumbSep>
        <BreadcrumbCurrent>
          {member.display_name || t('メンバー詳細', 'Member Detail')}
        </BreadcrumbCurrent>
      </Breadcrumb>

      {/* ──── Member Profile Card ──── */}
      <ProfileCard>
        <ProfileTop>
          <Avatar>{firstLetter}</Avatar>
          <ProfileInfo>
            <ProfileName>{member.display_name || '--'}</ProfileName>
            {member.position && <ProfileEmail>{member.position}</ProfileEmail>}
            <BadgeRow>
              <RoleBadge $admin={member.role === 'admin'}>
                {member.role === 'admin'
                  ? t('管理者', 'Admin')
                  : member.role === 'manager'
                  ? t('マネージャー', 'Manager')
                  : t('メンバー', 'Member')}
              </RoleBadge>
              <StatusBadge $bg={`${statusCfg.fallback}20`} $fg={statusCfg.fallback}>
                {statusLabel}
              </StatusBadge>
              {teamName && (
                <StatusBadge
                  $bg={`${statusCfg.fallback}00`}
                  $fg="inherit"
                  style={{
                    background: 'transparent',
                    border: `1px solid ${statusCfg.fallback}40`,
                  }}
                >
                  {teamName}
                </StatusBadge>
              )}
            </BadgeRow>
          </ProfileInfo>
          {member.type_code && (
            <TypeCodeBadge to={`/types/${member.type_code}`}>{member.type_code}</TypeCodeBadge>
          )}
        </ProfileTop>

        {member.struct_code && (
          <StructCodeDisplay>
            STRUCT CODE: {member.struct_code}
          </StructCodeDisplay>
        )}
      </ProfileCard>

      {/* ──── Diagnosis Data ──── */}
      {hasDiagnosis ? (
        <SectionCard>
          <SectionTitle>
            {t('行動特性分析結果', 'Behavioral Analysis Results')}
          </SectionTitle>
          <SpectrumBar
            axes={member.axes}
            normalize={Object.values(member.axes).every(
              (v) => typeof v === 'number' && v <= 1
            )}
            lang={lang}
          />
          {member.type_code && (
            <div
              style={{
                marginTop: 16,
                fontSize: '0.85rem',
                color: 'inherit',
                opacity: 0.7,
              }}
            >
              {t('行動特性タイプ', 'Behavioral Type')}: {member.type_code}
            </div>
          )}
        </SectionCard>
      ) : null}

      {/* ──── AI Behavioral Analysis ──── */}
      {hasDiagnosis && (personalAi || personalAiLoading) && (
        <SectionCard>
          <SectionTitle>
            {t('AI行動特性分析', 'AI Behavioral Analysis')}
          </SectionTitle>
          <PersonalAiReport data={personalAi} loading={personalAiLoading} />
        </SectionCard>
      )}

      {/* ──── Team Position Radar ──── */}
      {hasDiagnosis && member.team_id && teamOverlayData ? (
        <SectionCard>
          <SectionTitle>
            {t('チーム内ポジション', 'Team Position')}
          </SectionTitle>
          <ChartCenter>
            <RadarChart
              data={memberRadarData}
              size={300}
              overlays={[
                {
                  data: teamOverlayData,
                  color: '#8B5CF6',
                  opacity: 0.2,
                },
              ]}
            />
          </ChartCenter>
          <LegendRow>
            <LegendItem>
              <LegendDot $color="#6366F1" />
              <span>{member.display_name || t('このメンバー', 'This member')}</span>
            </LegendItem>
            <LegendItem>
              <LegendDot $color="#8B5CF6" />
              <span>{teamName || t('チーム平均', 'Team Average')}</span>
            </LegendItem>
          </LegendRow>
          <DiffLabel>
            {t('チーム平均との差分', 'Difference from Team Average')}
          </DiffLabel>
        </SectionCard>
      ) : hasDiagnosis && member.team_id && !teamOverlayData ? (
        <SectionCard>
          <SectionTitle>
            {t('チーム内ポジション', 'Team Position')}
          </SectionTitle>
          <EmptyState>
            {t(
              'チームの分析データがまだありません。チームメンバーの分析が完了すると表示されます。',
              'No team analysis data yet. This will display once team members complete their analysis.'
            )}
          </EmptyState>
        </SectionCard>
      ) : null}

      {/* ──── Link Diagnosis (if not yet diagnosed) ──── */}
      {!hasDiagnosis && (
        <LinkDiagnosisSection>
          <SectionTitle>
            {t('分析結果の紐付け', 'Link Analysis Result')}
          </SectionTitle>
          <LinkDescription>
            {t(
              'このメンバーにはまだ行動特性分析の結果が紐付けられていません。分析IDを入力して紐付けてください。',
              'This member does not have a behavioral analysis result linked yet. Enter the diagnosis ID to link it.'
            )}
          </LinkDescription>
          <InputRow>
            <TextInput
              type="text"
              placeholder={t('分析IDを入力', 'Enter diagnosis ID')}
              value={diagnosisIdInput}
              onChange={(e) => {
                setDiagnosisIdInput(e.target.value);
                setLinkSuccess(false);
              }}
              disabled={linkLoading}
            />
            <PrimaryButton
              onClick={handleLinkDiagnosis}
              disabled={linkLoading || !diagnosisIdInput.trim()}
            >
              {linkLoading
                ? t('処理中...', 'Processing...')
                : t('紐付ける', 'Link')}
            </PrimaryButton>
          </InputRow>
          {linkSuccess && (
            <SuccessMessage>
              {t(
                '分析結果を紐付けました。データを更新中です...',
                'Diagnosis linked successfully. Refreshing data...'
              )}
            </SuccessMessage>
          )}
        </LinkDiagnosisSection>
      )}
    </PageContainer>
  );
}

export default MemberDetailPage;
